import { Injectable } from '@angular/core';
import { HashMap, TranslocoService } from '@ngneat/transloco';
import { TranslocoLocaleService } from '@ngneat/transloco-locale';
import { firstValueFrom, Subscription } from 'rxjs';
import { ChipContent } from 'src/app/shared/models/chip-content/chip-content';
import { ChipContentOption } from 'src/app/shared/models/chip-content/chip-content-option';
import { NavigationService } from '../navigation/navigation.service';

@Injectable({
  providedIn: 'root',
})
/**
 * Translation service helps in translating objects
 */
export class TranslationService {
  locale: string;

  /**
   * @constructor
   */
  constructor(
    private readonly translocoService: TranslocoService,
    private readonly translocoLocaleService: TranslocoLocaleService,
    private readonly navigationService: NavigationService
  ) {
    this.locale = this.translocoLocaleService.getLocale();
  }

  /**
   * Translates object as if values where translatable keys
   * @param {any} obj to be translated
   * @param {Object<string, HashMap>?} params
   */
  async translateObject(obj: any, params?: { [key: string]: HashMap }) {
    for (const str in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, str)) {
        let param: HashMap | undefined;

        if (params && Object.prototype.hasOwnProperty.call(params, str)) {
          param = params[str];
        }

        obj[str] = await firstValueFrom(
          this.translocoService.selectTranslate(obj[str], param)
        );
      }
    }
  }

  /**
   * Translates object as if values where translatable keys
   * @param {any} obj to be translated
   * @param {Object<string, HashMap>?} params
   * @return {Subscription}
   */
  translateObject$(
    obj: any,
    params?: { [key: string]: HashMap }
  ): Subscription {
    const subsink = new Subscription();
    for (const str in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, str)) {
        let param: HashMap | undefined;

        if (params && Object.prototype.hasOwnProperty.call(params, str)) {
          param = params[str];
        }

        subsink.add(
          this.translocoService
            .selectTranslate(obj[str], param)
            .subscribe((translation) => {
              obj[str] = translation;
            })
        );
      }
    }
    return subsink;
  }

  /**
   * Translates a string as if value where translatable
   * @param {string} str
   * @param {HashMap?} params
   * @return {Promise<string>}
   */
  async translateString(str: string, params?: HashMap): Promise<string> {
    return await firstValueFrom(
      this.translocoService.selectTranslate(str, params)
    );
  }

  /**
   * Translates a chip content
   * @param {string} chipContents
   */
  async translateChipContentArray(chipContents: ChipContent[]) {
    for (const chipContent of chipContents) {
      this.translateString(chipContent.value as string).then((translated) => {
        chipContent.value = translated;
      });
      if (chipContent.options) {
        this.translateChipContentArrayOptions(chipContent.options);
      }
    }
  }

  /**
   * Translates a set of options from chip content
   * @param {ChipContentOption[]} options
   */
  async translateChipContentArrayOptions(options: ChipContentOption[]) {
    for (const option of options) {
      this.translateString(option.text as string).then((translated) => {
        option.text = translated;
      });
    }
  }

  /**
   * @param {string} translocoLang
   */
  setActiveLang(translocoLang?: string) {
    const currentLang = this.translocoService.getActiveLang();
    if (currentLang === translocoLang) {
      return;
    }
    if (translocoLang) {
      this.translocoService.setActiveLang(translocoLang);
    } else {
      this.translocoService.setActiveLang(this.locale);
    }
    this.navigationService.reload().then();
  }

  /**
   *
   * @return {'en' | 'es' | 'pt-BR'}
   */
  getActiveLang(): 'en' | 'es' | 'pt-BR' {
    const activeLang = this.translocoService.getActiveLang();
    if (!['en', 'es', 'pt-BR'].includes(activeLang)) {
      throw new Error(`unknown language is active: ${activeLang}`);
    }
    return activeLang as any;
  }

  /**
   * @param {string} n
   * @return {string}
   */
  localizeDate(n: string) {
    return this.translocoLocaleService.localizeDate(n).replace(/\//g, '.');
  }

  /**
   * @param {string} s
   * @param {number} n
   * @return {Promise<string>}
   */
  async translatePlural(s: string, n: number): Promise<string> {
    let sn = '';
    if (n === 0 || n === 1) {
      sn = `${s}-${n}`;
    } else {
      sn = `${s}-other`;
    }
    const translation: string = await firstValueFrom(
      this.translocoService.selectTranslate(sn)
    );
    return translation.replace(/<OTHER>/g, n.toString());
  }
}
